import * as React from "react";
import {
  CourseRatePrice,
  CourseRates,
  Days,
  GolfContract,
  GolfContractProduct,
  GolfContractProductRate,
} from "@kalabo/golfsavers-common";
import { Text } from "@uikit";
import dayjs from "dayjs";
import XE from "@components/common/xe";
import { useApp } from "@store/app";

interface IProps {
  rates: CourseRates;
  contracts: GolfContract[];
  weekend_days: Days;
}

// create me a function that takes in a Days object as well as the weekend_days object.
// The weekend_days object is a Days object that has the days that are considered weekend days flagged with true.
// if the Days object has any of the days that are flagged as weekend days, then return true.
// otherwise return false.

const isWeekendRate = (days: Days, weekend_days: Days) => {
  //get a list of the days that are flagged as weekend days
  let weekdays = Object.keys(weekend_days).filter((day: any) => {
    return weekend_days[day as keyof Days] === true;
  });

  // get a list of the days that are true in the days object
  let days_list = Object.keys(days).filter((day: any) => {
    return days[day as keyof Days] === true;
  });

  // if there is any overlap between the two lists, then return true
  let result = weekdays.some((day: any) => {
    return days_list.includes(day);
  });

  return result;
};

const isWeekdayRate = (days: Days, weekend_days: Days) => {
  //get a list of the days that are flagged as weekend days
  let weekdays = Object.keys(weekend_days).filter((day: any) => {
    return weekend_days[day as keyof Days] === false;
  });

  // get a list of the days that are true in the days object
  let days_list = Object.keys(days).filter((day: any) => {
    return days[day as keyof Days] === true;
  });

  // if there is any overlap between the two lists, then return true
  let result = weekdays.some((day: any) => {
    return days_list.includes(day);
  });

  return result;
};

const getWeekendHeader = (weekend_days: Days) => {
  // get all the weekend_days and return the ones that are true

  let weekends = Object.keys(weekend_days).filter((day: any) => {
    return weekend_days[day as keyof Days] === true;
  });

  // Rest of the code...

  const sortOrder = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  // sort the weekends
  weekends = weekends.sort((a, b) => {
    return sortOrder.indexOf(a) - sortOrder.indexOf(b);
  });

  // return result as first - last.  uppwer case first letter and only two letters for each day.
  return `${weekends[0].charAt(0).toUpperCase()}${weekends[0].slice(
    1,
    2
  )} - ${weekends[weekends.length - 1].charAt(0).toUpperCase()}${weekends[
    weekends.length - 1
  ].slice(1, 2)}`;
};

const getWeekdayHeader = (weekend_days: Days) => {
  // get all the weekend_days and return the ones that are false
  let weekdays = Object.keys(weekend_days).filter((day: any) => {
    return weekend_days[day as keyof Days] === false;
  });

  // Rest of the code...

  const sortOrder = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  // sort the weekdays
  weekdays = weekdays.sort((a, b) => {
    return sortOrder.indexOf(a) - sortOrder.indexOf(b);
  });

  // return result as first - last.  uppwer case first letter and only two letters for each day.
  return `${weekdays[0].charAt(0).toUpperCase()}${weekdays[0].slice(
    1,
    2
  )} - ${weekdays[weekdays.length - 1].charAt(0).toUpperCase()}${weekdays[
    weekdays.length - 1
  ].slice(1, 2)}`;
};

const CoursePrices = ({ rates, contracts, weekend_days }: IProps) => {
  const display_currency = useApp(($) => $.display_currency);
  const setShowCurrencyPicker = useApp(($) => $.setShowCurrencyPicker);

  const getExtraTitle = (type: string) => {
    switch (type) {
      case "CLUB_HIRE":
        return "Club Hire";
      case "SHOE_HIRE":
        return "Shoe Hire";
      case "UMBRELLA_HIRE":
        return "Umbrella Hire";
      case "SINGLE_CART":
        return "Single Cart";
      case "CADDIE":
        return "Caddie";
      case "NON_PLAYING_FOLLOWER":
        return "Non Playing Follower";
      case "RANGE_BALLS":
        return "Range Balls";
    }
  };

  let current_contracts = contracts.filter((contract) => {
    return (
      (dayjs().isAfter(dayjs(contract.start_date)) &&
        dayjs().isBefore(dayjs(contract.end_date))) ||
      dayjs().isBefore(dayjs(contract.start_date))
    );
  });

  if (current_contracts.length === 0) return null;

  if (!current_contracts) return null;

  return (
    <>
      <div className="mb-10 border-t border-grey3  pt-10 flex flex-col">
        <div className="flex justify-between items-center">
          <Text
            element="h2"
            size={21}
            weight="semibold"
            className="mb-[30px]"
            color="black"
          >
            Prices
          </Text>

          <Text size={13} leading={1.3} className="mb-5" color="black90">
            Showing prices in{" "}
            <span
              className="font-semibold text-15 underline cursor-pointer hover:text-blue"
              onClick={() => {
                setShowCurrencyPicker();
              }}
            >
              {display_currency.code}
            </span>
          </Text>
        </div>
        <div className="flex flex-col gap-10">
          <div>
            <Text
              element="h3"
              size={17}
              leading={1.3}
              weight="semibold"
              className="mb-5"
              color="black90"
            >
              Green Fees
            </Text>
            {current_contracts
              .sort(
                (a: GolfContract, b: GolfContract) =>
                  dayjs(a.start_date).unix() - dayjs(b.start_date).unix()
              )
              .map((contract: GolfContract, idx: any) => {
                const start = dayjs(contract.start_date);
                const end = dayjs(contract.end_date);

                // group by golf golf_contract_products.product_id

                const groupedData = contract?.products.reduce((acc, item) => {
                  const group = acc?.find(
                    (a) => a.product_id === item.product_id
                  );
                  if (group) {
                    group.rates.push(item);
                  } else {
                    acc.push({ product_id: item.product_id, rates: [item] });
                  }
                  return acc;
                }, []);

                return (
                  <table key={idx} className="w-full mb-7">
                    <thead className="">
                      <tr className="border-b border-grey3">
                        <th className="px-[10px] pb-2 text-left w-6/12">
                          <Text size={13} weight="semibold">
                            {start.format("DD MMM YY")} -{" "}
                            {end.format("DD MMM YY")}
                          </Text>
                        </th>

                        <th className="px-[10px] pb-2 text-left w-3/12">
                          <Text size={13} weight="semibold">
                            {getWeekdayHeader(weekend_days)}
                          </Text>
                        </th>
                        <th className="px-[10px] pb-2 text-left w-3/12">
                          <Text size={13} weight="semibold">
                            {getWeekendHeader(weekend_days)}
                          </Text>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedData?.map((obj: any, idx: any) => {
                        let products = obj.rates as GolfContractProduct[];
                        // flattern into one array the product golf_contract_product_rates
                        let rates = products.reduce(
                          (acc: GolfContractProductRate[], item) => {
                            acc.push(...item.rates);
                            return acc;
                          },
                          []
                        );

                        let weekday_rates = rates.filter(
                          (rate: GolfContractProductRate) => {
                            return isWeekdayRate(
                              rate.availability_days,
                              weekend_days
                            );
                          }
                        );

                        // get cheapest weekday rate
                        let cheapest_weekday_rate = weekday_rates.reduce(
                          (acc: GolfContractProductRate, item) => {
                            if (acc.sell_price > item.sell_price) {
                              return item;
                            }
                            return acc;
                          },
                          weekday_rates[0]
                        );

                        let weekend_rates = rates.filter(
                          (rate: GolfContractProductRate) => {
                            return isWeekendRate(
                              rate.availability_days,
                              weekend_days
                            );
                          }
                        );

                        let cheapest_weekend_rate = weekend_rates.reduce(
                          (acc: GolfContractProductRate, item) => {
                            if (acc.sell_price > item.sell_price) {
                              return item;
                            }
                            return acc;
                          },
                          weekend_rates[0]
                        );

                        return (
                          <tr
                            key={idx}
                            className="border-b border-grey2 bg-green/5"
                          >
                            <td className="px-[10px]  py-[10px] text-left w-6/12">
                              {products[0].product?.name?.en}
                            </td>

                            <td className="px-[10px]  py-[10px] text-left w-3/12">
                              <Text size={15} leading={1.5}>
                                {cheapest_weekday_rate?.sell_price > 0 ? (
                                  <>
                                    from{" "}
                                    <XE
                                      base_currency={contracts[0].currency_code}
                                      hide_decimal
                                      currency={display_currency.code}
                                      value={cheapest_weekday_rate?.sell_price}
                                    />
                                  </>
                                ) : (
                                  <>N/A</>
                                )}
                              </Text>
                            </td>
                            <td className="px-[10px]  py-[10px] text-left w-3/12">
                              <Text size={15} leading={1.5}>
                                {cheapest_weekend_rate?.sell_price > 0 ? (
                                  <>
                                    from{" "}
                                    <XE
                                      base_currency={contracts[0].currency_code}
                                      hide_decimal
                                      currency={display_currency.code}
                                      value={cheapest_weekend_rate?.sell_price}
                                    />
                                  </>
                                ) : (
                                  <>N/A</>
                                )}
                              </Text>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })}
          </div>
          {rates?.extras?.enabled === "YES" &&
            rates?.extras &&
            rates?.extras?.prices.length > 0 && (
              <div>
                <Text
                  element="h3"
                  size={17}
                  leading={1.3}
                  weight="semibold"
                  className="mb-5"
                  color="black90"
                >
                  Extras
                </Text>
                <table className="w-full">
                  <thead className="">
                    <tr className="border-b border-grey3">
                      <th className="px-[10px] pb-2 text-left w-9/12">
                        <Text size={13} weight="semibold">
                          Item
                        </Text>
                      </th>
                      <th className="px-[10px] pb-2">
                        <Text size={13} weight="semibold w-3/12">
                          Price
                        </Text>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates?.extras?.prices?.map(
                      (extra: CourseRatePrice, idx: any) => {
                        return (
                          <tr
                            key={idx}
                            className="border-b border-grey2 bg-[#FCFCFC]"
                          >
                            <td className="px-[10px]  py-[10px] text-left w-4/6">
                              <Text size={15} leading={1.5}>
                                {getExtraTitle(extra.type)}
                              </Text>
                              <Text size={13} leading={1.5} color="black70">
                                {extra.notes}
                              </Text>
                            </td>
                            <td className="px-[10px]  py-[10px] align-top">
                              <Text size={15} leading={1.5}>
                                {Number(extra.price) <= 0 ? (
                                  <>FREE</>
                                ) : (
                                  <>
                                    <XE
                                      base_currency={extra.currency}
                                      hide_decimal
                                      currency={display_currency.code}
                                      value={
                                        Math.round(Number(extra.price) * 100) /
                                        100
                                      }
                                    />
                                  </>
                                )}
                              </Text>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            )}
          {rates?.equipment?.enabled === "YES" &&
            rates?.equipment &&
            rates?.equipment?.prices?.length > 0 && (
              <div>
                <Text
                  element="h3"
                  size={17}
                  leading={1.3}
                  weight="semibold"
                  className="mb-5"
                  color="black90"
                >
                  Equipment Hire
                </Text>
                <table className="w-full">
                  <thead className="">
                    <tr className="border-b border-grey3">
                      <th className="px-[10px] pb-2 text-left w-9/12">
                        <Text size={13} weight="semibold">
                          Item
                        </Text>
                      </th>
                      <th className="px-[10px] pb-2  w-3/12">
                        <Text size={13} weight="semibold">
                          Price
                        </Text>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates?.equipment?.prices.map(
                      (extra: CourseRatePrice, idx: any) => {
                        return (
                          <tr
                            key={idx}
                            className="border-b border-grey2 bg-[#FCFCFC]"
                          >
                            <td className="px-[10px]  py-[10px] text-left">
                              <Text size={15} leading={1.5}>
                                {getExtraTitle(extra.type)}
                              </Text>
                              <Text size={13} leading={1.5} color="black70">
                                {extra.notes}
                              </Text>
                            </td>
                            <td className="px-[10px]  py-[10px] align-top">
                              <Text size={15} leading={1.5}>
                                <XE
                                  base_currency={extra.currency}
                                  currency={display_currency.code}
                                  hide_decimal
                                  value={
                                    Math.round(Number(extra.price) * 100) / 100
                                  }
                                />

                                {extra.to_price && (
                                  <>
                                    {" "}
                                    -{" "}
                                    <XE
                                      base_currency={extra.currency}
                                      currency={display_currency.code}
                                      hide_decimal
                                      value={
                                        Math.round(
                                          Number(extra.to_price) * 100
                                        ) / 100
                                      }
                                    />
                                  </>
                                )}
                              </Text>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
                <Text size={11} leading={1.3} color="black60" className="mt-1">
                  Prices for equipment hire are set by the course and subject to
                  change. Billed in {rates?.equipment?.prices[0]?.currency}.
                </Text>
              </div>
            )}
        </div>
      </div>
    </>
  );
};

export default CoursePrices;
